/**
 * El grafo de la deuda de datos de unit economics.
 *
 * Es la pieza que convierte "faltan 6 datos" en "cargá este primero": cada
 * métrica vacía sabe por qué está vacía, qué insumo hay que ir a buscar y qué
 * otras métricas quedan legibles cuando ese insumo aparece.
 *
 * Todo es puro y se calcula sobre el payload: los conteos, la cadena y el
 * titular de la palanca salen de los valores que mandó la API, no de constantes.
 * Lo único declarado a mano son dos cosas que el contrato todavía no modela:
 *
 *  1. `DERIVADAS` — qué métrica sale de qué otras. El contrato guarda la fórmula
 *     como texto (`'LTV / CAC'`), que sirve para mostrarla pero no para saber
 *     que LTV:CAC se destraba cargando CAC.
 *  2. `INSUMOS` — qué hay que ir a buscar para poder cargar una métrica manual.
 *     Es el paso anterior al número, y `MetricDefinition` no tiene campo para
 *     eso. Cuando lo tenga, estas dos tablas se borran y salen del catálogo.
 */
import {
  MENOS,
  SOURCE_LABELS,
  listaEs,
  type MetricDefinition,
  type MetricValue,
} from '@ops/contracts'

/**
 * Las 11 métricas del panel, en el orden en que se leen: primero las que se
 * pueden leer hoy, después la cadena de la deuda.
 *
 * Nueve llegan en `unitEconomics.metricas`; `arr` y `nps` salen del catálogo de
 * `/api/metrics`. Se listan acá y no se toman "las que vengan" para que agregar
 * una métrica al catálogo no cambie sin aviso la pantalla de Revenue.
 */
export const SLUGS_PANEL: readonly string[] = [
  'arr',
  'gross_margin',
  'ticket_promedio',
  'churn_mensual',
  'churn_anual',
  'ltv',
  'cac',
  'ltv_cac',
  'payback',
  'nrr',
  'nps',
]

/**
 * Métricas que el panel deriva de otras: su acción nunca es "cargá el
 * resultado", es "cargá el insumo".
 */
export const DERIVADAS: Readonly<Record<string, readonly string[]>> = {
  ltv_cac: ['ltv', 'cac'],
  payback: ['cac', 'ticket_promedio'],
}

/** Qué hay que ir a buscar antes de poder cargar cada métrica manual. */
export const INSUMOS: Readonly<Record<string, string>> = {
  gross_margin: 'los costos directos del período (infra, modelos y soporte)',
  ltv: 'el ARPU y el churn por segmento',
  cac: 'el gasto de adquisición del período (sueldos comerciales y pauta)',
  nrr: 'doce meses de cohorte cerrada',
  nps: 'una encuesta a la base de clientes',
}

export interface FilaMetrica {
  slug: string
  nombre: string
  metric: MetricValue
  /** Definición del catálogo. null cuando `/api/metrics` no la trajo. */
  definicion: MetricDefinition | null
  /** true = tiene dato para el período. */
  conDato: boolean
  /** true = carga manual y no es derivada: se puede cargar desde el panel. */
  cargable: boolean
  /** Slugs de los que se deriva. Vacío cuando no es derivada. */
  derivadaDe: readonly string[]
  /** De esos slugs, los que hoy no tienen dato. */
  faltan: readonly string[]
  /** Insumo a conseguir antes de poder cargarla. null cuando no aplica. */
  insumo: string | null
  /** Por qué está vacía, en concreto. null cuando tiene dato. */
  motivo: string | null
  /** Qué slug hay que cargar para destrabarla. null si no hay acción posible. */
  slugAccion: string | null
  /** Carga manual de más de 30 días: el dato existe pero no se puede defender. */
  vencida: boolean
}

/**
 * Arma las filas del panel cruzando los valores del período con el catálogo.
 *
 * Un slug del panel que no vino en el payload se omite en silencio en vez de
 * inventarse una fila vacía: si la API dejó de servir una métrica, la pantalla
 * no tiene que fingir que la conoce.
 */
export function filasDe(
  metricas: readonly MetricValue[],
  definiciones: readonly MetricDefinition[],
): FilaMetrica[] {
  const porSlug = new Map(metricas.map((m) => [m.slug, m]))
  const defs = new Map(definiciones.map((d) => [d.slug, d]))

  const conDatoDe = (slug: string): boolean =>
    (porSlug.get(slug)?.valor ?? null) !== null
  const nombreDe = (slug: string): string =>
    porSlug.get(slug)?.nombre ?? defs.get(slug)?.nombre ?? slug

  const filas: FilaMetrica[] = []

  for (const slug of SLUGS_PANEL) {
    const metric = porSlug.get(slug)
    if (metric === undefined) continue

    const definicion = defs.get(slug) ?? null
    const derivadaDe = DERIVADAS[slug] ?? []
    const esDerivada = derivadaDe.length > 0
    const source = definicion?.source ?? metric.provenance.source
    const cargable = !esDerivada && source === 'manual'
    const conDato = metric.valor !== null
    const faltan = derivadaDe.filter((dep) => !conDatoDe(dep))
    const insumo = INSUMOS[slug] ?? null

    filas.push({
      slug,
      nombre: metric.nombre,
      metric,
      definicion,
      conDato,
      cargable,
      derivadaDe,
      faltan,
      insumo,
      motivo: conDato
        ? null
        : motivoDe({
            esDerivada,
            formula: metric.formula ?? definicion?.formula ?? null,
            faltan: faltan.map(nombreDe),
            cargable,
            insumo,
            source,
          }),
      slugAccion: null, // se completa abajo, cuando están todas las filas
      vencida: conDato && metric.provenance.freshness === 'expired',
    })
  }

  // La acción de una derivada es cargar su insumo, así que necesita ver el
  // resto de las filas para saber cuál de sus dependencias es cargable.
  const porFila = new Map(filas.map((f) => [f.slug, f]))
  return filas.map((fila) => ({
    ...fila,
    slugAccion: fila.conDato ? null : accionDe(fila, porFila, new Set()),
  }))
}

function motivoDe(input: {
  esDerivada: boolean
  formula: string | null
  faltan: readonly string[]
  cargable: boolean
  insumo: string | null
  source: MetricValue['provenance']['source']
}): string {
  const { esDerivada, formula, faltan, cargable, insumo, source } = input
  const sale = formula === null ? '' : ` Sale de ${formula}.`

  if (esDerivada) {
    if (faltan.length > 0) {
      return `No se carga a mano.${sale} Falta ${listaEs([...faltan])}.`
    }
    return `No se carga a mano.${sale} Sus insumos ya están cargados: falta que la ingesta la escriba.`
  }

  if (cargable) {
    const que = insumo === null ? 'el dato' : insumo
    return `Se carga a mano y nunca se cargó. Hay que traer ${que}.`
  }

  return `La fuente ${SOURCE_LABELS[source]} todavía no escribió este período.`
}

/** Primer slug cargable que destraba esta fila. Recursivo y a prueba de ciclos. */
function accionDe(
  fila: FilaMetrica,
  porFila: ReadonlyMap<string, FilaMetrica>,
  vistos: Set<string>,
): string | null {
  if (vistos.has(fila.slug)) return null
  vistos.add(fila.slug)

  if (fila.cargable) return fila.slug

  for (const dep of fila.faltan) {
    const hijo = porFila.get(dep)
    if (hijo === undefined) continue
    const slug = accionDe(hijo, porFila, vistos)
    if (slug !== null) return slug
  }

  return null
}

// ---------------------------------------------------------------------------
// Deuda de datos
// ---------------------------------------------------------------------------

export interface Deuda {
  total: number
  sinDato: number
  conDato: number
  /** Cargas manuales de más de 30 días: el dato está, pero vencido. */
  vencidas: number
  /** Métricas con dato y semáforo fuera de target. */
  enRojo: number
  enAtencion: number
}

export function deudaDe(filas: readonly FilaMetrica[]): Deuda {
  return {
    total: filas.length,
    sinDato: filas.filter((f) => !f.conDato).length,
    conDato: filas.filter((f) => f.conDato).length,
    vencidas: filas.filter((f) => f.vencida).length,
    enRojo: filas.filter((f) => f.conDato && f.metric.health === 'critical')
      .length,
    enAtencion: filas.filter((f) => f.conDato && f.metric.health === 'warning')
      .length,
  }
}

// ---------------------------------------------------------------------------
// Cadena de dependencias
// ---------------------------------------------------------------------------

export interface EslabonHijo {
  fila: FilaMetrica
  /** true = cargando la base de la cadena, esta métrica ya queda calculable. */
  listoAlCargar: boolean
  /** Lo que le sigue faltando además de la base. */
  faltanAdemas: readonly string[]
}

export interface Cadena {
  base: FilaMetrica
  hijos: readonly EslabonHijo[]
  /** Cuántas métricas quedan legibles si se carga la base, hoy. */
  cierra: number
}

/**
 * Una cadena por cada métrica cargable que hoy está vacía. No se declaran a
 * mano: si mañana CAC se carga, su cadena desaparece sola.
 */
export function cadenasDe(filas: readonly FilaMetrica[]): Cadena[] {
  const vacias = filas.filter((f) => !f.conDato)

  const cadenas = filas
    .filter((f) => !f.conDato && f.cargable)
    .map((base) => {
      const hijos: EslabonHijo[] = vacias
        .filter((f) => f.derivadaDe.includes(base.slug))
        .map((fila) => {
          const faltanAdemas = fila.faltan.filter((dep) => dep !== base.slug)
          return {
            fila,
            listoAlCargar: faltanAdemas.length === 0,
            faltanAdemas: faltanAdemas.map(
              (dep) => filas.find((f) => f.slug === dep)?.nombre ?? dep,
            ),
          }
        })

      return {
        base,
        hijos,
        cierra: 1 + hijos.filter((h) => h.listoAlCargar).length,
      }
    })

  // La que más destraba primero: es el orden en el que hay que trabajar.
  return cadenas.sort((a, b) => b.cierra - a.cierra)
}

export interface Palanca {
  titulo: string
  texto: string
  /** Slug a cargar. null cuando no hay nada que cargar desde el panel. */
  slugAccion: string | null
  /** true = no queda deuda; el bloque va en verde y sin acción. */
  resuelto: boolean
}

/**
 * El titular de la sección: cuál es el dato que más destraba y qué pasa cuando
 * se carga. Todas las cifras salen de las filas, así que el texto no puede
 * quedar desactualizado respecto de la tabla que tiene abajo.
 */
export function palancaDe(
  filas: readonly FilaMetrica[],
  cadenas: readonly Cadena[],
): Palanca {
  const deuda = deudaDe(filas)

  if (deuda.sinDato === 0) {
    return {
      titulo: 'No queda deuda de datos',
      texto: `Las ${deuda.total} métricas tienen dato. Las manuales vencen a los 30 días: cuando eso pase, vuelven a aparecer acá.`,
      slugAccion: null,
      resuelto: true,
    }
  }

  const mejor = cadenas[0]
  if (mejor === undefined) {
    const nombres = filas.filter((f) => !f.conDato).map((f) => f.nombre)
    return {
      titulo: 'Lo que falta no se destraba con una carga',
      texto: `${listaEs(nombres)} no dependen de otra métrica del panel: hay que ir a buscar el dato a su fuente.`,
      slugAccion: null,
      resuelto: false,
    }
  }

  const cierra = [
    mejor.base.nombre,
    ...mejor.hijos.filter((h) => h.listoAlCargar).map((h) => h.fila.nombre),
  ]
  const aUnaCarga = mejor.hijos.filter((h) => !h.listoAlCargar)

  const partes = [
    `Cargar ${mejor.base.nombre} deja ${mejor.cierra} de las ${deuda.sinDato} métricas sin dato en condiciones de leerse: ${listaEs(cierra)}.`,
  ]
  for (const hijo of aUnaCarga) {
    partes.push(
      `${hijo.fila.nombre} queda a una carga de distancia: le falta ${listaEs([...hijo.faltanAdemas])}.`,
    )
  }
  if (mejor.base.insumo !== null) {
    partes.push(`Para cargarlo hay que traer ${mejor.base.insumo}.`)
  }

  return {
    titulo: `${mejor.base.nombre} es la palanca de esta pantalla`,
    texto: partes.join(' '),
    slugAccion: mejor.base.slug,
    resuelto: false,
  }
}

// ---------------------------------------------------------------------------
// Insumos publicados "para poder recalcular a mano"
// ---------------------------------------------------------------------------

/** Tope de decimales. Más que esto es ruido de punto flotante, no precisión. */
const MAX_DECIMALES_INSUMO = 4

/**
 * Un porcentaje que la pantalla publica **como insumo de una cuenta**, con los
 * decimales que hagan falta para reproducirla.
 *
 * `formatMetricValue(_, 'percent')` redondea a un decimal, que es lo correcto
 * para una tarjeta de KPI y falso para un insumo: la línea de la cadena de
 * dependencias promete "para poder recalcular a mano" y publicaba el churn
 * mensual como **0,6%**. Con ese número, `1 − (1 − churn)^12` da 7,0%, mientras
 * tres bloques más arriba la misma pantalla dice que el churn anual es 6,6%. El
 * valor real que manda la API es 0,57%, y con ese sí da 6,63% → 6,6%.
 *
 * Se imprime la menor cantidad de decimales que **no pierde** información
 * respecto del número que mandó el servidor: 6,63 sale "6,63%" y 6,6 sale
 * "6,6%", sin ceros de relleno.
 */
export function formatInsumoPct(valor: number | null): string {
  if (valor === null) return 'sin dato'

  const exacto = Number(valor.toFixed(MAX_DECIMALES_INSUMO))
  let digits = 1
  while (digits < MAX_DECIMALES_INSUMO && Number(valor.toFixed(digits)) !== exacto) {
    digits += 1
  }

  const cuerpo = new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    useGrouping: true,
  }).format(Math.abs(exacto))

  return `${exacto < 0 ? MENOS : ''}${cuerpo}%`
}
